import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { Formik, Field } from 'formik';
import moment from 'moment';
import { toast } from 'react-toastify';
import Layout from './DashboardLayout';
import { TimesheetTable } from '../components/timesheet';
import {
  teamService,
  projectService,
  timesheetService,
  tasksService,
} from '../services';
import Loading from '../components/Loader';
import { fetchOnBoardProgress } from '../actions/user';
import {
  TeamMembersDropdown,
  ProjectListDropdown,
  DateRangePickerField,
  WorkSessionTypeDropdown,
} from '../components/common';
import { DATE_RANGE_OPTIONS, TIMESHEET_DATE_RANGE } from '../constants';
import { getUserData } from '../helpers';
import { sendAmplitudeData } from '../utilities/amplitude.js';

class SummarizedTimesheet extends Component {
  state = {
    isLoading: true,
    teamMembersList: [],
    projectsList: [],
    tasksList: [],
    startDate: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    timesheet: [],
    timesheetMetadata: {},
    isLoadingTimesheet: true,
    isFetchingTimesheetData: false,
    isSummarizedReport: true,
    isUpdatingTeamsList: false,
    isUpdatingProjectsList: false,
    isUpdatingTasksList: false,
    isSendingEmail: false,
    numberOfDaysSelected: 1,
    daysLimit: 90,
    selectedTeamMember: 'all',
  };

  pageHeading = {
    preTitle: 'Timesheet',
    title: 'Summarized Timesheet',
  };

  componentDidMount() {
    const { startDate, endDate } = this.state;
    const { userRole } = this.props;
    const memberId = getUserData('_id');
    teamService
      .get_team_members_and_projects({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
      })
      .then((result) => {
        const { teamMembers, projects } = result.data;
        this.setState((state) => ({
          ...state,
          isLoading: false,
          projectsList: [...projects],
          teamMembersList: [...teamMembers],
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });

    this.getTaskData({
      teamOwnerId: localStorage.getItem('teamOwnerId'),
    });

    this.handleGenerateData({
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      fromDate: startDate,
      toDate: endDate,
      project: 'all',
      task: 'all',
      teamMember: userRole !== 'member' ? 'all' : memberId,
      workSessionType: 'all',
    });
  }

  handleDateSelect = (event, picker) => {
    this.setState((state) => ({
      ...state,
      startDate: moment(picker.startDate).format('YYYY-MM-DD'),
      endDate: moment(picker.endDate).format('YYYY-MM-DD'),
    }));
  };

  handleDateRangeValidation = (start, end) => {
    const { daysLimit } = this.state;
    const daysSelected = Math.abs(moment(start).diff(moment(end), 'd'));
    if (daysSelected > daysLimit) {
      toast.error(
        `${TIMESHEET_DATE_RANGE.LIMIT} ${TIMESHEET_DATE_RANGE.ERROR}`
      );
    }
    this.setState((state) => ({
      ...state,
      numberOfDaysSelected: daysSelected,
    }));
  };

  getProjectData = (member) => {
    this.setState((state) => ({
      ...state,
      isUpdatingProjectsList: true,
    }));

    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      summaryList: true,
    };
    if (member) {
      params.filter = JSON.stringify({
        teamMembers: [member],
      });
    }
    projectService
      .get_projects_data(params)
      .then((response) => {
        const {
          data: { projects },
        } = response;
        this.setState((state) => ({
          ...state,
          projectsList: [...projects],
          isUpdatingProjectsList: false,
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isUpdatingProjectsList: false,
        }));
      });
  };

  getTaskData = (data) => {
    this.setState((state) => ({
      ...state,
      isUpdatingTasksList: true,
    }));
    tasksService
      .get_tasks_data(data)
      .then((result) => {
        const { tasks } = result.data;
        this.setState((state) => ({
          ...state,
          isLoading: false,
          tasksList: [...tasks],
          isUpdatingTasksList: false,
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
          isUpdatingTasksList: false,
        }));
      });
  };

  // Update project list on changing team members selection
  handleTeamMemberChange = (event) => {
    this.getProjectData(event.target.value);
    const taskParams = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      teamMemberId: event.target.value,
    };
    if (taskParams.teamMemberId === 'all') {
      delete taskParams.teamMemberId;
    }
    this.getTaskData(taskParams);
    this.setState((state) => ({
      ...state,
      selectedTeamMember: event.target.value,
    }));
  };

  handleProjectChange = (event) => {
    const { selectedTeamMember } = this.state;
    const taskParams = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      projectId: event.target.value,
      teamMemberId: selectedTeamMember,
    };
    if (taskParams.projectId === 'all') {
      delete taskParams.projectId;
    }
    if (taskParams.teamMemberId === 'all') {
      delete taskParams.teamMemberId;
    }
    this.getTaskData(taskParams);
  };

  handleGenerateData = (formdata) => {
    this.setState((state) => ({
      ...state,
      isFetchingTimesheetData: true,
    }));
    const { startDate, endDate } = this.state;
    const params = {
      teamOwnerId: formdata.teamOwnerId,
      teamMember: [formdata.teamMember].flat(),
      project: [formdata.project].flat(),
      task: [formdata.task].flat(),
      workSessionType: formdata.workSessionType,
      fromDate: startDate.concat(' 00:00:00'),
      toDate: endDate.concat(' 23:59:59'),
      summarizedTimesheet: true,
    };

    if (formdata.teamMember === 'all') {
      delete params.teamMember;
    }
    if (formdata.project === 'all') {
      delete params.project;
    }
    if (formdata.task === 'all') {
      delete params.task;
    }
    if (formdata.workSessionType === 'all') {
      delete params.workSessionType;
    }

    // Store form data on formParams for sending email report
    this.setState(() => ({
      formParams: { ...params },
    }));
    const { getOnBoardingProgress } = this.props;
    timesheetService
      .get_timesheet_data(params)
      .then((response) => {
        const subscriptionDetails = getUserData('subscriptionDetails');
        if (subscriptionDetails.currentPlan === 'trial') {
          getOnBoardingProgress();
          sendAmplitudeData('View report', {
            page: 'Summarized timesheet',
            status: 'Success',
          });
          if (
            response &&
            'data' in response &&
            'metadata' in response.data &&
            response.data.metadata.total > 0
          ) {
            sendAmplitudeData('Added time', {
              page: 'Summarized timesheet',
              status: 'Success',
            });
          }
        }
        const {
          data: { metadata, timesheetDetails },
        } = response;

        this.setState((state) => ({
          ...state,
          isFetchingTimesheetData: false,
          timesheet: timesheetDetails,
          timesheetMetadata: metadata,
          isLoadingTimesheet: false,
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isFetchingTimesheetData: false,
          isLoadingTimesheet: false,
        }));
      });
  };

  handleEmailReport = () => {
    const { formParams } = this.state;
    this.setState((state) => ({
      ...state,
      isSendingEmail: true,
    }));
    timesheetService
      .email_timesheet_report({ ...formParams })
      .then((response) => {
        toast.success('Timesheet has been emailed successfully');
        this.setState((state) => ({
          ...state,
          isSendingEmail: false,
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isSendingEmail: false,
        }));
      });
  };

  render() {
    const {
      teamMembersList,
      projectsList,
      tasksList,
      startDate,
      endDate,
      timesheet,
      timesheetMetadata,
      isLoadingTimesheet,
      isFetchingTimesheetData,
      isSummarizedReport,
      isLoading,
      isUpdatingTeamsList,
      isUpdatingProjectsList,
      isUpdatingTasksList,
      isSendingEmail,
      numberOfDaysSelected,
      daysLimit,
    } = this.state;
    const { userRole } = this.props;
    const memberId = getUserData('_id');

    // Team owner ID
    const teamOwnerId = localStorage.getItem('teamOwnerId');
    const { isMobileTracking } = getUserData('defaultTeam');
    return (
      <Layout pageHeading={this.pageHeading}>
        <Formik
          initialValues={{
            teamOwnerId,
            teamMember: userRole !== 'member' ? 'all' : memberId,
            project: 'all',
            task: 'all',
            fromDate: startDate,
            toDate: endDate,
          }}
          onSubmit={(values) => {
            this.handleGenerateData(values);
          }}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              <Row>
                <Col md={6} lg={3}>
                  <TeamMembersDropdown
                    teamMembersList={teamMembersList}
                    handleChange={formikprops.handleChange}
                    handleTeamMemberChange={this.handleTeamMemberChange}
                    isDependentFiled
                    isUpdatingTeamsList={isUpdatingTeamsList}
                  />
                </Col>
                <Col md={6} lg={3}>
                  <ProjectListDropdown
                    projectsList={projectsList}
                    isUpdatingProjectsList={isUpdatingProjectsList}
                    handleChange={formikprops.handleChange}
                    handleProjectChange={this.handleProjectChange}
                    isDependedField
                  />
                </Col>
                <Col md={6} lg={3}>
                  <Form.Group controlId="task">
                    <Form.Label>Task</Form.Label>
                    <Field
                      className="form-select"
                      as="select"
                      name="task"
                      disabled={isUpdatingTasksList || !tasksList.length > 0}
                    >
                      <option value="all">All</option>
                      {tasksList &&
                        tasksList.length > 0 &&
                        tasksList.map((item, index) => (
                          <option key={`${index}-${item._id}`} value={item._id}>
                            {item.task}
                          </option>
                        ))}
                    </Field>
                  </Form.Group>
                </Col>
                <Col md={6} lg={3}>
                  <DateRangePickerField
                    handleDateSelect={this.handleDateSelect}
                    startFrom="last 7 days"
                    showRangeOptions
                    preDefinedRanges={DATE_RANGE_OPTIONS}
                    handleDateRangeValidation={this.handleDateRangeValidation}
                    isValidate
                    numberOfDaysSelected={numberOfDaysSelected}
                    daysLimit={daysLimit}
                  />
                </Col>
                {isMobileTracking && (
                  <Col md={6} lg={3}>
                    <WorkSessionTypeDropdown />
                  </Col>
                )}
                <Col md={6} lg={3}>
                  <Form.Group>
                    <Form.Label>&nbsp;</Form.Label>
                    <div>
                      <Button
                        variant="primary"
                        type="Submit"
                        disabled={
                          isUpdatingTeamsList ||
                          isUpdatingProjectsList ||
                          numberOfDaysSelected > daysLimit
                        }
                      >
                        Generate
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        {!isLoadingTimesheet && (
          <TimesheetTable
            timesheetMetadata={timesheetMetadata}
            timesheet={timesheet}
            startDate={startDate}
            endDate={endDate}
            isSummarizedReport={isSummarizedReport}
            handleEmailReport={this.handleEmailReport}
            userRole={userRole}
            isMobileTracking={isMobileTracking}
            isSendingEmail={isSendingEmail}
          />
        )}
        {/* Loader */}
        {(isLoading || isFetchingTimesheetData) && <Loading contentAreaOnly />}
      </Layout>
    );
  }
}

const mapStateToProps = (state) => {
  const { userReducer } = state;
  return {
    userRole: userReducer.role,
  };
};

const mapDispatchToProps = () => ({
  getOnBoardingProgress: fetchOnBoardProgress,
});

SummarizedTimesheet.propTypes = {
  userRole: PropTypes.string,
  getOnBoardingProgress: PropTypes.func,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps()
)(SummarizedTimesheet);
